import { doc } from '@firebase/firestore';
import { setDoc } from 'firebase/firestore';
import { useFormik } from 'formik';
import { useTranslation } from 'next-i18next';
import { ReactElement, useState } from 'react';
import tw, { styled } from 'twin.macro';
import * as Yup from 'yup';
import { firestore } from 'core/clientApp';
import { phoneNumberFormatter } from 'core/util/number';
import { Checkbox, checkStyle, ErrorMsg, ItemName } from 'core/view/Form';
import ModalWrap from 'core/view/ModalWrap.module';

const simpleHash = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash; // Convert to 32bit integer
  }
  return new Uint32Array([hash])[0].toString(36);
};

const SubmitButton = styled.button`
  ${tw`bg-[#667eff] rounded-[10px] h-[52px] md:h-[60px]`};
  ${tw`mt-[30px] md:mt-[40px]`};
  ${tw`text-[18px] text-white w-full`};
`;

interface ConsultingModalProps {
  isOpen: boolean;
  close: () => void;
}

const ConsultingModal = (props: ConsultingModalProps): ReactElement => {
  return (
    <section>
      <ModalWrap open={props.isOpen} onClose={props.close}>
        <Form onClose={props.close} />
      </ModalWrap>
    </section>
  );
};

type Props = {
  onClose: () => void;
};

type AjaxStatusProps = 'success' | 'idle' | 'error' | 'loading';

const Form = (props: Props) => {
  const [ajaxStatus, setAjaxStatus] = useState<AjaxStatusProps>('idle');
  const [isSuccess, setISSuccess] = useState(false);
  const { t } = useTranslation('membership');

  const PartnersFormSchema = Yup.object().shape({
    company_name: Yup.string().required(t('contactModal.error.isRequired')),
    name: Yup.string().required(t('contactModal.error.isRequired')),
    phone: Yup.string().required(t('contactModal.error.isRequired')),
    email: Yup.string().required(t('contactModal.error.isRequired')),
    inquiry: Yup.string().required(t('contactModal.error.isRequired')),
    agree: Yup.boolean()
      .required(t('contactModal.error.isRequired'))
      .oneOf([true], t('contactModal.error.agree')),
  });

  const formik = useFormik({
    initialValues: {
      company_name: '',
      name: '',
      phone: '',
      email: '',
      inquiry: '',
      agree: false,
    },
    validationSchema: PartnersFormSchema,
    initialTouched: {},
    onSubmit: async () => {
      if (!formik.isValid) return;
      if (ajaxStatus === 'loading') return;

      const call = async () => {
        const { ...param } = formik.values;

        const hash = simpleHash(
          `${param.name}${param.phone}${new Date().getTime()}`,
        );
        const timestamp = `${Date.now().toString()}-${hash}`;

        const collectionName =
          process.env.NEXT_PUBLIC_ENV_NODE === 'production'
            ? 'selfstorage_inquiry'
            : 'selfstorage_inquiry_test';

        const _ConsultingList = doc(
          firestore,
          `${collectionName}/${timestamp}`,
        );

        try {
          setAjaxStatus('loading');

          await setDoc(_ConsultingList, {
            ...param,
            createAt: new Date(),
          });

          setAjaxStatus('idle');
          setISSuccess(true);
        } catch (error) {
          setAjaxStatus('idle');
          alert(t('contactModal.error.submit'));
          props.onClose && props.onClose();
        }
      };

      call();

      setAjaxStatus('idle');
    },
  });

  const renderErrorMessage = (field: keyof typeof formik.touched) => {
    return formik.touched[field] && <ErrorMsg>{formik.errors[field]}</ErrorMsg>;
  };

  return (
    <div
      css={[
        tw`w-full`,
        tw`max-h-screen overflow-auto relative md:w-[600px]`,
        tw`font-family['NanumSquare']`,
      ]}
    >
      <section css={[isSuccess ? tw`` : tw`hidden`]}>
        <button
          type="button"
          css={tw`w-[17px] h-[17px] absolute top[20px] right[20px]`}
          onClick={() => {
            props.onClose();
          }}
        >
          <BtnClose />
        </button>
        <div css={[tw`flex flex-col`, tw`p-[20px] md:p-[30px]`]}>
          <article css={[tw`flex items-center justify-center`, tw`py-[100px]`]}>
            <div css={[tw`text-center`]}>
              <svg
                css={[tw`mx-auto`]}
                width="48"
                height="49"
                viewBox="0 0 48 49"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  y="0.692383"
                  width="48"
                  height="48"
                  rx="24"
                  fill="white"
                />
                <rect
                  y="0.692383"
                  width="48"
                  height="48"
                  rx="24"
                  fill="#5B809B"
                  fillOpacity="0.1"
                />
                <path
                  d="M14 26.0424L21.625 32.6924L34 18.6924"
                  stroke="#5B809B"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              <header
                css={[tw`mt-[16px] text-[18px] font-weight[800] text-[#333]`]}
              >
                {t('contactModal.success.header')}
              </header>
              <p css={[tw`text-[14px] text-[#666]`]}>
                {t('contactModal.success.desc')}
              </p>
            </div>
          </article>

          <SubmitButton
            css={[tw`bg-[#5B809B]`]}
            onClick={() => {
              props.onClose && props.onClose();
            }}
          >
            확인
          </SubmitButton>
        </div>
      </section>

      <form
        onSubmit={formik.handleSubmit}
        css={[isSuccess ? tw`hidden` : tw``]}
      >
        <button
          type="button"
          css={tw`w-[17px] h-[17px] absolute top[20px] right[20px]`}
          onClick={() => {
            props.onClose();
          }}
        >
          <BtnClose />
        </button>
        <div css={[tw`flex flex-col`, tw`p-[20px] md:p-[30px]`]}>
          <header
            css={[
              tw`text-[14px]  text-[#333]`,
              tw`text-center`,
              tw`px-[30px] line-height[1.3em]`,
            ]}
          >
            {t('contactModal.header.desc') && (
              <>
                {t('contactModal.header.desc')}
                <br />
              </>
            )}
            <span css={[tw`inline-block font-bold text-[30px] mt-[10px]`]}>
              {t('contactModal.header.title')}
            </span>
          </header>

          <div css={[tw`h-[30px]`]} />

          <section css={[tw`flex flex-col gap-[10px]`]}>
            <div css={[tw`flex flex-col gap-[4px]`, tw`w-full`]}>
              <div css={[tw`w-[110px]`]}>
                <ItemName isRequired={true}>
                  {t('contactModal.form.company.label')}
                </ItemName>
              </div>
              <div css={[tw`w-full`]}>
                <input
                  css={[tw`w-full border-[1px] p-[12px 15px] rounded-[5px]`]}
                  placeholder={t('contactModal.form.company.placeholder')}
                  {...formik.getFieldProps('company_name')}
                />
                {renderErrorMessage('company_name')}
              </div>
            </div>

            <div css={[tw`flex flex-col gap-[4px]`, tw`w-full`]}>
              <div css={[tw`w-[110px]`]}>
                <ItemName isRequired={true}>
                  {t('contactModal.form.person.label')}
                </ItemName>
              </div>
              <div css={[tw`w-full`]}>
                <input
                  css={[tw`w-full border-[1px] p-[12px 15px] rounded-[5px]`]}
                  placeholder={t('contactModal.form.person.placeholder')}
                  {...formik.getFieldProps('name')}
                />
                {renderErrorMessage('name')}
              </div>
            </div>

            <div css={[tw`flex flex-col gap-[4px]`, tw`w-full`]}>
              <div css={[tw`w-[110px]`]}>
                <ItemName isRequired={true}>
                  {t('contactModal.form.phone.label')}
                </ItemName>
              </div>
              <div css={[tw`w-full`]}>
                <input
                  css={[tw`w-full border-[1px] p-[12px 15px] rounded-[5px]`]}
                  placeholder={t('contactModal.form.phone.placeholder')}
                  {...formik.getFieldProps('phone')}
                  onChange={(e) => {
                    formik.setValues({
                      ...formik.values,
                      phone: phoneNumberFormatter(e.target.value),
                    });
                  }}
                />

                {renderErrorMessage('phone')}
              </div>
            </div>

            <div css={[tw`flex flex-col gap-[4px]`, tw`w-full`]}>
              <div css={[tw`w-[110px]`]}>
                <ItemName isRequired={true}>
                  {t('contactModal.form.email.label')}
                </ItemName>
              </div>
              <div css={[tw`w-full`]}>
                <input
                  css={[tw`w-full border-[1px] p-[12px 15px] rounded-[5px]`]}
                  placeholder={t('contactModal.form.email.placeholder')}
                  {...formik.getFieldProps('email')}
                />

                {renderErrorMessage('email')}
              </div>
            </div>

            <div css={[tw`flex flex-col gap-[4px]`, tw`w-full`]}>
              <div css={[tw`w-[110px]`, tw`flex self-start`, tw`mt-[10px]`]}>
                <ItemName isRequired={true}>
                  {t('contactModal.form.inquiry.label')}
                </ItemName>
              </div>
              <div css={[tw`w-full`, tw`relative`]}>
                <div css={[tw`relative`]}>
                  <textarea
                    maxLength={300}
                    rows={4}
                    css={[
                      tw`w-full border-[1px] p-[12px 15px] rounded-[5px]`,
                      tw`pb-[28px]`,
                    ]}
                    placeholder={t('contactModal.form.inquiry.placeholder')}
                    {...formik.getFieldProps('inquiry')}
                    style={{
                      resize: 'none',
                    }}
                  />

                  <span
                    css={[
                      tw`absolute right-[16px] bottom-[9px]`,
                      tw`text-[#333]`,
                    ]}
                  >
                    {formik.values.inquiry.length}
                    <span css={[tw`text-[#999]`, tw`ml-[5px]`]}>{`/ 300`}</span>
                  </span>
                </div>

                {renderErrorMessage('inquiry')}
              </div>
            </div>
          </section>

          <div tw="h-[16px]" />

          <label tw="text-[#999999]" css={checkStyle}>
            <input type="checkbox" {...formik.getFieldProps('agree')} />
            <Checkbox />
            <span>{t('contactModal.form.agree.title')}</span>
          </label>
          <AgreementDesc>
            <ul tw="text-[#666666] ml-[20px]">
              <li>{t('contactModal.form.agree.ul.l1')}</li>
              <li>{t('contactModal.form.agree.ul.l2')}</li>
              <li>{t('contactModal.form.agree.ul.l3')}</li>
            </ul>
          </AgreementDesc>

          {renderErrorMessage('agree')}

          <SubmitButton
            type="submit"
            css={[
              !formik.isValid || !formik.dirty
                ? tw`bg-[#ddd]`
                : tw`bg-[#5B809B]`,
            ]}
            className={'gtag-submit-partners-from-business'}
          >
            {t('contactModal.form.submit')}
          </SubmitButton>
        </div>
      </form>
    </div>
  );
};

export default ConsultingModal;

const BtnClose = () => {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 17 17"
      onClick={() => {
        sessionStorage.removeItem('consultingBranchName');
      }}
    >
      <g fill="none" fillRule="evenodd">
        <g transform="translate(-3 -2)">
          <rect
            fill="#999"
            transform="rotate(45 11.309 10.114)"
            x=".309"
            y="9.364"
            width="22"
            height="1.5"
            rx=".75"
          />
          <rect
            fill="#999"
            transform="rotate(-45 11.309 10.114)"
            x=".309"
            y="9.364"
            width="22"
            height="1.5"
            rx=".75"
          />
        </g>
      </g>
    </svg>
  );
};

const AgreementDesc = styled.section`
  ul {
    ${tw`mt-[3px]`}
  }

  li {
    ${tw`text-[12px]`}
  }

  li {
    ${tw`pl-[15px] relative`};
    &::before {
      content: '';
      position: absolute;
      top: 8px;
      left: 4px;
      width: 4px;
      height: 4px;
      background-color: #666;
      ${tw`rounded-full`}
    }
  }
`;
